import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router'
import { getEvent } from '@/data/calendarApi'
import { addTask } from '@/data/tasks'
import { usePaths } from '@/hooks/usePaths'
import { usePeople, type People } from '@/hooks/usePeople'
import { useShownEvents, type ShownEvent } from '@/hooks/useEvents'
import { waitForTask } from '@/lib/reveal'
import { fire } from '@/lib/writeError'
import { fmtDate, fmtRange, today } from '@/logic/dates'
import { taskFromEvent } from '@/logic/eventTask'
import { stripHtml } from '@/logic/events'
import { newTaskDefaults } from '@/logic/newTask'
import { itemsOnDay, orderAtEnd } from '@/logic/plan'
import { getToken, useCalendar } from '@/store/calendar'
import { useHousehold } from '@/store/household'
import { useTasks } from '@/store/tasks'
import { openEventCard, openQuickAdd, showToast, useUi } from '@/store/ui'
import { Avatar, PairAvatars } from './Avatar'
import { Button } from './Button'
import { LinkedText } from './LinkedText'
import { Sheet } from './Sheet'
import { eventTone, whoseCalendar } from './eventTone'
import { CalendarIcon, ClockIcon, ExternalIcon, PinIcon, TextIcon, VideoIcon } from './icons'
import './Events.css'

// Google events are read-only in Cuplzap: a count in each day's heading that
// opens into one line of chips, and a card on tap. Each is coloured by whose
// calendar it is on; a calendar marked Both is neutral.

/** The owner's letter, or both letters for a calendar marked Both. */
export function OwnerMark({ ev, people }: { ev: ShownEvent; people: People }) {
  if (ev.owner === 'me') return <Avatar uid={people.meUid} people={people} small />
  if (ev.owner === 'other' && people.other) return <Avatar uid={people.other.uid} people={people} small />
  return <PairAvatars people={people} small />
}

/** A day heading's events as one count: the icon, the number, a dot for each person with an event. Never an event's name. */
export function EventCount({ events, open, onToggle, fixed = false }: { events: ShownEvent[]; open: boolean; onToggle: () => void; fixed?: boolean }) {
  const people = usePeople()
  if (!events.length) return null
  const tones = [...new Set(events.map((e) => eventTone(e.owner, people)))].sort()
  const label = `${events.length} event${events.length === 1 ? '' : 's'}`
  const inner = (
    <>
      <CalendarIcon size={13} weight={2.2} />
      <span>{events.length}</span>
      <span className="evcount__dots" aria-hidden="true">
        {tones.map((t) => (
          <span key={t} className={`evcount__dot ev--${t}`} />
        ))}
      </span>
    </>
  )
  // with Events on Show all every day's events are already showing: a plain count, nothing to switch
  if (fixed)
    return (
      <span className="evcount" role="img" aria-label={label}>
        {inner}
      </span>
    )
  return (
    <button type="button" className={`evcount evcount--toggle ${open ? 'evcount--open' : ''}`} aria-expanded={open} aria-label={label} onClick={onToggle}>
      {inner}
    </button>
  )
}

/**
 * One line of chips under a heading, all-day first then by time; it scrolls sideways and never wraps.
 * While it has more than fits, a sideways swipe on it scrolls it and never changes the week.
 */
export function EventChips({ events }: { events: ShownEvent[] }) {
  const people = usePeople()
  if (!events.length) return null
  return (
    <div className="evchips">
      <div className="evchips__scroll" data-sheet-swipe="off" data-swipe-scroller>
        {events.map((ev) => (
          <button key={`${ev.calendarId}:${ev.id}`} type="button" className={`evchip ev--${eventTone(ev.owner, people)}`} onClick={() => openEventCard({ calendarId: ev.calendarId, eventId: ev.id })}>
            <OwnerMark ev={ev} people={people} />
            {ev.allDay ? `All day · ${ev.title}` : `${ev.startTime} ${ev.title}`}
            {ev.location && <PinIcon className="evchip__pin" size={12} weight={2.2} title="Has a location" />}
          </button>
        ))}
      </div>
      <span className="evchips__fade" aria-hidden="true" />
    </div>
  )
}

/** The read-only event card, the same on the Plan and Calendar tabs. */
export function EventCard() {
  const open = useUi((s) => s.openEvent)
  const events = useShownEvents()
  const calendars = useCalendar((s) => s.calendars)
  const people = usePeople()
  const paths = usePaths()
  const navigate = useNavigate()
  const settings = useHousehold((s) => s.userDoc?.settings)
  const [making, setMaking] = useState(false)
  const ev = useMemo(() => (open ? (events.find((e) => e.calendarId === open.calendarId && e.id === open.eventId) ?? null) : null), [open, events])
  const close = () => openEventCard(null)
  if (!open || !ev) return null
  const calName = calendars.find((c) => c.id === ev.calendarId)?.name ?? 'Calendar'
  const when = ev.allDay
    ? ev.startDate === ev.endDate
      ? `${fmtDate(ev.startDate)} · All day`
      : `${fmtRange(ev.startDate, ev.endDate)} · All day`
    : ev.startDate === ev.endDate
      ? `${fmtDate(ev.startDate)} · ${ev.startTime} – ${ev.endTime}`
      : `${fmtDate(ev.startDate)} ${ev.startTime} – ${fmtDate(ev.endDate)} ${ev.endTime}`
  const addForDay = () => {
    close()
    openQuickAdd({ plannedDate: ev.startDate, hint: `For ${ev.title} on ${fmtDate(ev.startDate)}` })
  }
  // writes the task at once and opens its edit panel; one of a series asks Google for the series' pattern first
  const makeTask = async () => {
    setMaking(true)
    let rrule: string[] | null = null
    let missedPattern = false
    if (ev.seriesId) {
      try {
        rrule = (await getEvent(getToken, ev.calendarId, ev.seriesId)).recurrence ?? null
      } catch {
        missedPattern = true
      }
    }
    const fields = taskFromEvent(ev, ev.description ? stripHtml(ev.description) : null, rrule)
    const { id, written } = addTask(paths, {
      ...fields,
      ...newTaskDefaults(settings, paths.uid),
      order: orderAtEnd(itemsOnDay(useTasks.getState().all, fields.dueDate, today())),
    })
    fire(written)
    setMaking(false)
    close()
    if (ev.seriesId && fields.kind === 'once') showToast(missedPattern ? "Couldn't read how this event repeats. Set Repeats if it should." : "This event's repeat doesn't fit Cuplzap's patterns. Set Repeats if it should.")
    await waitForTask(id)
    navigate(`/task/${id}`)
  }
  return (
    <Sheet open onClose={close} heading="Event" variant="dialog">
      <div className="evcard">
        <h2 className="evcard__title">{ev.title}</h2>
        <div className="evcard__line">
          <ClockIcon size={16} />
          <span>{when}</span>
        </div>
        <div className="evcard__line">
          <OwnerMark ev={ev} people={people} />
          <span>{whoseCalendar(ev, people, calName)}</span>
        </div>
        {ev.videoLink && (
          <div className="evcard__line">
            <VideoIcon size={16} />
            <a href={ev.videoLink} target="_blank" rel="noopener noreferrer">
              Join video call
            </a>
          </div>
        )}
        {ev.location && (
          <div className="evcard__line">
            <PinIcon size={16} />
            <span className="evcard__text"><LinkedText text={ev.location} /></span>
          </div>
        )}
        {ev.description && (
          <div className="evcard__line evcard__line--top">
            <TextIcon size={16} />
            <span className="evcard__text evcard__desc"><LinkedText text={stripHtml(ev.description)} /></span>
          </div>
        )}
        <div className="evcard__actions">
          <Button variant="primary" block onClick={() => void makeTask()} disabled={making}>
            {making ? 'Creating the task…' : 'Create a task from this event'}
          </Button>
          <Button block onClick={addForDay}>
            Add a task for this day
          </Button>
          {ev.htmlLink && (
            <a className="btn btn--secondary btn--block evcard__ext" href={ev.htmlLink} target="_blank" rel="noopener noreferrer">
              Open in Google Calendar
              <ExternalIcon size={16} />
            </a>
          )}
        </div>
      </div>
    </Sheet>
  )
}
